import React, { useState } from "react";
import { injectIntl } from "react-intl";
import cx from 'classnames';
import withStyles from "isomorphic-style-loader/withStyles";
import { useMutation } from "@apollo/client";
import { Container } from "react-bootstrap";

import Loader from "../../components/Common/Loader/Loader";
import useQueryResponse from "../../lib/hooks/useQueryResponse";
import showToaster from "../../helpers/toastrMessgaes/showToaster";
import { getEmergencyContact, addEmergencyContact, deleteEmergencyContact } from "../../lib/graphql";
import messages from "../../locale/messages";
import bt from '../../components/commonStyle.css';
import s from "./TrustedContacts.css";

// Web cannot read the device address book, so trusted contacts are added via a manual name + phone
// form (expected web idiom). Parity with the mobile app's Trusted Contacts screen.
const MAX_CONTACTS = 5;
const emptyForm = { contactName: '', phoneNumber: '' };

const TrustedContacts = (props) => {
    const { intl: { formatMessage } } = props;

    const { data, loading, refetch } = useQueryResponse({
        query: getEmergencyContact,
        ssr: true,
        fetchPolicy: 'network-only'
    });

    const [addContact] = useMutation(addEmergencyContact);
    const [removeContact] = useMutation(deleteEmergencyContact);

    const [form, setForm] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);

    const results = data?.getEmergencyContact?.results || [];
    const limitReached = results.length >= MAX_CONTACTS;

    const openAdd = () => {
        setForm(emptyForm);
        setShowForm(true);
    };

    const closeForm = () => {
        setForm(emptyForm);
        setShowForm(false);
    };

    const handleSave = async () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            showToaster({ messageId: 'networkFailed', toasterType: 'error' });
            return;
        }
        if (!form?.contactName || !form?.contactName.trim() || !form?.phoneNumber || !form?.phoneNumber.trim()) {
            showToaster({ messageId: 'errorMessage', requestContent: formatMessage(messages.contactDetailsWarning), toasterType: 'error' });
            return;
        }
        setSaving(true);
        try {
            const { data: resp } = await addContact({
                variables: {
                    data: JSON.stringify([{ contactName: form.contactName.trim(), phoneNumber: form.phoneNumber.trim() }])
                }
            });
            setSaving(false);
            if (resp?.addEmergencyContact?.status === 200) {
                showToaster({ messageId: 'errorMessage', requestContent: formatMessage(messages.contactAdded), toasterType: 'success' });
                closeForm();
                await refetch();
            } else {
                showToaster({ messageId: 'errorMessage', requestContent: resp?.addEmergencyContact?.errorMessage, toasterType: 'error' });
            }
        } catch (error) {
            setSaving(false);
            showToaster({ messageId: 'errorMessage', requestContent: error?.message, toasterType: 'error' });
        }
    };

    const handleDelete = async (item) => {
        if (typeof window !== 'undefined' && !window.confirm(formatMessage(messages.deleteContactConfirm))) {
            return;
        }
        try {
            const { data: resp } = await removeContact({ variables: { contactId: Number(item?.id) } });
            if (resp?.deleteEmergencyContact?.status === 200) {
                showToaster({ messageId: 'errorMessage', requestContent: formatMessage(messages.contactDeleted), toasterType: 'success' });
                await refetch();
            } else {
                showToaster({ messageId: 'errorMessage', requestContent: resp?.deleteEmergencyContact?.errorMessage, toasterType: 'error' });
            }
        } catch (error) {
            showToaster({ messageId: 'errorMessage', requestContent: error?.message, toasterType: 'error' });
        }
    };

    return (
        <div className={cx('subPageContainer')}>
            <Container>
                <div className='subPageWrapper'>
                    <div className={s.headerRow}>
                        <p className={s.pageTitle}>{formatMessage(messages.trustedContacts)}</p>
                        {!showForm && !limitReached && (
                            <button type="button" className={cx(bt.btnPrimary, s.addBtn)} onClick={openAdd}>
                                {formatMessage(messages.addContact)}
                            </button>
                        )}
                    </div>

                    {limitReached && !showForm && (
                        <p className={s.emptyText}>{formatMessage(messages.maxContactsReached)}</p>
                    )}

                    {showForm && (
                        <div className={s.formCard}>
                            <p className={s.formTitle}>{formatMessage(messages.addContact)}</p>

                            <div className={s.fieldGroup}>
                                <label className={bt.commonLabelText}>{formatMessage(messages.contactNameLabel)}</label>
                                <input
                                    type="text"
                                    className={cx(bt.formControlInput, 'form-control')}
                                    value={form?.contactName || ''}
                                    maxLength={50}
                                    placeholder={formatMessage(messages.contactNameLabel)}
                                    onChange={(e) => setForm((prev) => ({ ...prev, contactName: e.target.value }))}
                                />
                            </div>

                            <div className={s.fieldGroup}>
                                <label className={bt.commonLabelText}>{formatMessage(messages.contactPhoneLabel)}</label>
                                <input
                                    type="tel"
                                    className={cx(bt.formControlInput, 'form-control')}
                                    value={form?.phoneNumber || ''}
                                    maxLength={20}
                                    placeholder={formatMessage(messages.contactPhoneLabel)}
                                    onChange={(e) => setForm((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                                />
                            </div>

                            <div className={s.formBtnGroup}>
                                <button type="button" className={cx(bt.btnSecondary, s.cancelBtn)} onClick={closeForm}>
                                    {formatMessage(messages.cancelLabel)}
                                </button>
                                <Loader
                                    type={"button"}
                                    buttonType={"button"}
                                    show={saving}
                                    label={formatMessage(messages.saveContact)}
                                    className={cx(bt.btnPrimary, 'loaderButtonFlex')}
                                    handleClick={handleSave}
                                />
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <p className={s.emptyText}>{formatMessage(messages.loadingText)}</p>
                    ) : results.length > 0 ? (
                        <ul className={s.addressList}>
                            {results.map((item) => (
                                <li key={item?.id} className={s.addressItem}>
                                    <div className={s.addressInfo}>
                                        <span className={s.addressName}>{item?.contactName}</span>
                                        <span className={s.addressText}>{item?.phoneNumber}</span>
                                    </div>
                                    <div className={s.addressActions}>
                                        <button type="button" className={cx(s.linkBtn, s.deleteLink)} onClick={() => handleDelete(item)}>
                                            {formatMessage(messages.deleteContact)}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className={s.emptyText}>{formatMessage(messages.noTrustedContacts)}</p>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default injectIntl(withStyles(bt, s)(TrustedContacts));
